/* eslint-disable parog-web/no-glint-expect-error-directive */
import type { TOC } from '@ember/component/template-only';
import {
  validateDate,
  validateNumber,
  validatePresence
} from 'ember-changeset-validations/validators';
import type { Payload as ForcPayload } from 'parog-web/components/forc/abstract/journal-event-based-component';
import type { Signature as ProviderSig } from 'parog-web/components/forc/provider';
import BaseMedicalHistoryFormComponent from 'parog-web/components/medical-history/abstract/base-edition-form';
import AccordionBlock from 'parog-web/components/medical-history/accordion-block';
import type Patient from 'parog-web/models/patient';
import validateSometimes from 'parog-web/validators/sometimes';
import MedicalHistoryBaseEditionForm from './base-edition-form';
import NephroMrcPreTransplantAssessmentResults from './nephro/mrc/pre-transplant-assessment-results-fields';
import NephroMrcSocialSituation from './nephro/mrc/social-situation-fields';
import NephroNotDonorPathology from './nephro/not-donor/pathology-fields';
import NephroTransplantPathology from './nephro/transplant/pathology-fields';

import type { TemplateOnlyComponent } from '@ember/component/template-only';
import dayjs from 'dayjs';
import NephroDonorDiagnostic from './nephro/donor/diagnostic-fields';
import NephroGenericDiagnostic from './nephro/generic/diagnostic-fields';
import NephroMrcDiagnostic from './nephro/mrc/diagnostic-fields';
import DialysisSection from './nephro/mrc/dialysis-fields';
import NephroNotDonorDiagnostic from './nephro/not-donor/diagnostic-fields';
import NephroTransplantDiagnostic from './nephro/transplant/diagnostic-fields';

const PathologyFields = {
  'not-donor': NephroNotDonorPathology,
  mrc: NephroNotDonorPathology,
  transplant: NephroTransplantPathology
};

const DiagnosticFields = {
  'not-donor': NephroNotDonorDiagnostic,
  mrc: NephroMrcDiagnostic,
  transplant: NephroTransplantDiagnostic,
  donor: NephroDonorDiagnostic,
  'nephro-generic': NephroGenericDiagnostic
};

const DialysisFields = {
  mrc: DialysisSection,
  'nephro-generic': DialysisSection
};

const PreTransplantAssesmentResultsSection = <template>
  <AccordionBlock
    @title='Résultats du bilan pré greffe'
    @isActive={{false}}
    class='pt-[1em]'
    data-test-pre-transplant-assessment-results-section
  >
    <NephroMrcPreTransplantAssessmentResults @form={{@form}} />
  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    form: ProviderSig['Blocks']['content'][0];
  };
}>;

const SocialSituationSection = <template>
  <AccordionBlock
    @title='Situation sociale'
    @isActive={{false}}
    class='pt-[1em]'
    data-test-social-situation-section
  >
    <NephroMrcSocialSituation @form={{@form}} @patient={{@patient}} />
  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    patient: Patient;
    form: ProviderSig['Blocks']['content'][0];
  };
}>;

export default class MedicalHistoryFormComponent extends BaseMedicalHistoryFormComponent {
  override journalEventProperties = [
    ...this.sharedProperties,
    ...this.renalPathologyProperties,
    ...this.pulmonaryPathologyProperties
  ];

  override get defaultModel() {
    return this.patient.healthCentre.preTransplant
      ? { preTransplantFolder: true }
      : {};
  }

  get diagnostic(): TemplateOnlyComponent {
    return DiagnosticFields[this.patho ?? ''];
  }

  get dialysis(): TemplateOnlyComponent {
    return DialysisFields[this.patho ?? ''];
  }

  get pathology(): TemplateOnlyComponent {
    return PathologyFields[this.patho ?? ''];
  }

  get pulmonaryPathologyProperties(): string[] {
    return [
      'transplantedOrgan',
      'underlyingDisease',
      'transplantDate',
      'transplantRank',
      'cytomegalovirusStatus'
    ];
  }

  get renalPathologyProperties(): string[] {
    return [
      'transplantDate',
      'transplantRank',
      'nephropatie',
      'donorType',
      'immunologicalRisk',
      'incompatibleABO',
      'donorCreatinine',
      'abmClassification',
      'creatinineThreeMonthsAfterTransplant',
      'rejectsFirstYear',
      'mrcStage',
      'hemodialysisType',
      'mrcPreexistingTransplant',
      'transplantedOrgan',
      'cytomegalovirusStatus',
      'preTransplantFolder',
      'registrationTransplantWaitingList',
      'assessmentRealisation',
      'assessmentResult',
      'professionalActivity',
      'maritalStatus',
      'housing',
      'lifestyle',
      'dialysisType',
      'dialysisCareLocation',
      'dialysisStartDate',
      'dialysisMachineType',
      'dialysisCareType'
    ];
  }

  override get validations(): Record<string, unknown> {
    return {
      mrcStage: validateSometimes(
        [
          validatePresence({
            presence: true,
            message: 'Le stade de la MRC ne peut être vide'
          })
        ],
        () => {
          return this.args.patient.isMrc;
        }
      ),
      dialysisCareLocation: validateSometimes(
        [
          validatePresence({
            presence: true,
            message: 'Le lieu de prise en charge ne peut être vide'
          })
        ],
        () => {
          return this.changeset.dialysisType === 'hemodialysis';
        }
      ),

      creatinineThreeMonthsAfterTransplant: validateNumber({
        gte: 20,
        lte: 2000,
        allowBlank: true,
        description: 'La quantité de créatinine après 3 mois'
      }),
      transplantDate: [
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date',
          onOrAfter: new Date('01/01/1900')
        }),
        validateDate({
          allowBlank: true,
          message:
            'Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date',
          onOrBefore: dayjs().endOf('day').toDate()
        })
      ]
    };
  }

  override computePayload(): ForcPayload {
    const payload = super.computePayload();

    if (!this.args.patient.isMrc) {
      delete payload.mrcStage;
      delete payload.mrcPreexistingTransplant;
    }
    if (!this.args.patient.isTransplanted) {
      delete payload.transplantedOrgan;
    }

    return payload;
  }

  override retrieveModel(): Record<string, unknown> {
    const model = super.retrieveModel();

    if (!this.isLoadedFromJe) {
      model.transplantedOrgan = 'kidney';
    }

    return model;
  }

  <template>
    <MedicalHistoryBaseEditionForm
      {{! @glint-expect-error }}
      @forcComponent={{this}}
      @patient={{@patient}}
      as |mh|
    >
      <mh.FurSection />
      <mh.FurDetailsSection
        @diagnostic={{this.diagnostic}}
        @dialysis={{this.dialysis}}
      />
      <mh.ComorbiditiesAllergiesSection />
      <mh.PathologySection @pathology={{this.pathology}} />
      <mh.VaccinesSection />

      {{#if @patient.isMrc}}
        <PreTransplantAssesmentResultsSection @form={{mh.form}} />
        <SocialSituationSection @form={{mh.form}} @patient={{@patient}} />
      {{/if}}

      <mh.CommentsSection />
    </MedicalHistoryBaseEditionForm>
  </template>
}
